import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslations } from 'next-intl'
import { get } from 'lodash'

import Button from '@/shared/v2/shared/components/Button'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import Input from '@/shared/v2/shared/components/Input'
import PasswordInput from '@/shared/v2/shared/components/PasswordInput'
import { Field } from '@hello-aria/ui/field'
import Select from '@/shared/v2/shared/components/Select'
import LoadingButton from '@/shared/v2/shared/components/LoadingButton'
import Dialog from '@/shared/v2/shared/components/Dialog'
import type { CreateSubscription } from '@/shared/models/subscriptionModels'
import { getSubscribableEvents } from '@/shared/models/subscriptionModels'
import { useCreateSubscription } from '@/modules/subscriptions/hooks/useSubscriptions'
import { resolveInputState } from '@/shared/utils/inputUtils'
import { useDialogState } from '@/shared/hooks/useDialogState'
import ExternalLink from '@/shared/v2/shared/components/ExternalLink'
import { useSetToast } from '@/shared/hooks/useSetToast'
import PrivilegesButton from '@/shared/v2/shared/components/PrivilegesButton'
import { useFlags } from '@/shared/utils/flags/clientFlags'

const CreateSubscriptionDialog = () => {
    const flags = useFlags()
    const rootTranslations = useTranslations('root')
    const webhooksTranslations = useTranslations('webhooks')
    const toasterTranslations = useTranslations('toasters')
    const setToast = useSetToast()

    const formMethods = useForm<CreateSubscription>()
    const {
        register,
        reset,
        handleSubmit,
        watch,
        formState: { errors },
        control
    } = formMethods

    const createSubscription = useCreateSubscription()

    const { isOpen, onOpenChange } = useDialogState({
        onClose: () => reset(),
        shouldCloseIf: createSubscription.status === 'success'
    })

    const onCreateSubscription = async (payload: CreateSubscription) => {
        try {
            await createSubscription.mutateAsync(payload)
            setToast({
                title: toasterTranslations('createSubscription.positive.title'),
                description: toasterTranslations('createSubscription.positive.description'),
                intent: 'success'
            })
        } catch (error) {
            setToast({
                title: toasterTranslations('createSubscription.negative.title'),
                description: toasterTranslations('createSubscription.negative.description'),
                intent: 'error'
            })
        }
    }

    const subscribableEvents = getSubscribableEvents(flags)

    return (
        <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
            <Dialog.Trigger asChild>
                <PrivilegesButton data-testid="__create-subscription-trigger__" intent="primary">
                    {webhooksTranslations('actions.createSubscription')}
                </PrivilegesButton>
            </Dialog.Trigger>

            <Dialog.Content title={webhooksTranslations('createSubscription.title')} className="max-w-[700px]">
                <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(onCreateSubscription)}>
                        <Dialog.Body>
                            <div className="grid grid-cols-1 gap-y-5">
                                <div>
                                    <Paragraph variant="medium">
                                        {webhooksTranslations('createSubscription.description')}
                                    </Paragraph>

                                    <ExternalLink href="https://docs.helloaria.eu/docs/webhook">
                                        {webhooksTranslations('createSubscription.subDescription')}
                                    </ExternalLink>
                                </div>

                                <div className="grid grid-cols-1 gap-y-3">
                                    <Input
                                        data-testid="__create-subscription-endpoint__"
                                        label={webhooksTranslations('createSubscription.fields.endpoint.label')}
                                        placeholder={webhooksTranslations(
                                            'createSubscription.fields.endpoint.placeholder'
                                        )}
                                        required
                                        state={resolveInputState(
                                            watch('options.endpoint'),
                                            get(errors, 'options.endpoint')
                                        )}
                                        message={errors.options?.endpoint?.message}
                                        {...register('options.endpoint', {
                                            required: {
                                                value: true,
                                                message: rootTranslations('errors.inputs.required')
                                            }
                                        })}
                                    />

                                    <Field
                                        label={webhooksTranslations('createSubscription.fields.secret.label')}
                                        error={errors.options?.secret?.message}
                                    >
                                        <PasswordInput
                                            data-testid="__create-subscription-secret__"
                                            placeholder={webhooksTranslations(
                                                'createSubscription.fields.secret.placeholder'
                                            )}
                                            {...register('options.secret', {
                                                minLength: {
                                                    value: 16,
                                                    message: rootTranslations('errors.inputs.secretLength')
                                                }
                                            })}
                                        />
                                    </Field>

                                    <Controller
                                        control={control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: rootTranslations('errors.inputs.required')
                                            }
                                        }}
                                        name="event"
                                        render={({ field: { onChange, value } }) => {
                                            return (
                                                <Select
                                                    required
                                                    data-testid="__create-subscription-event-type__"
                                                    label={webhooksTranslations(
                                                        'createSubscription.fields.eventType.label'
                                                    )}
                                                    placeholder={webhooksTranslations(
                                                        'createSubscription.fields.eventType.placeholder'
                                                    )}
                                                    state={resolveInputState(watch('event'), errors['event'])}
                                                    message={errors?.event?.message}
                                                    isInModal={true}
                                                    onChange={(option) => onChange(option?.value)}
                                                    value={
                                                        value
                                                            ? {
                                                                  label: value,
                                                                  value
                                                              }
                                                            : undefined
                                                    }
                                                    options={subscribableEvents.map((event) => ({
                                                        label: event,
                                                        value: event
                                                    }))}
                                                />
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                        </Dialog.Body>

                        <Dialog.Footer>
                            <Dialog.Close asChild>
                                <Button intent={'secondary'} type={'button'}>
                                    {webhooksTranslations('createSubscription.buttons.cancel')}
                                </Button>
                            </Dialog.Close>

                            <LoadingButton
                                data-testid="__create-subscription-submit__"
                                intent="primary"
                                type="submit"
                                isLoading={createSubscription.isPending}
                            >
                                {webhooksTranslations('createSubscription.buttons.submit')}
                            </LoadingButton>
                        </Dialog.Footer>
                    </form>
                </FormProvider>
            </Dialog.Content>
        </Dialog.Root>
    )
}

export default CreateSubscriptionDialog
